import { useMedplum } from "@medplum/react-hooks";
import { type FC, type ReactNode, useMemo, useRef } from "react";
import { DataStoreClient } from "../core/client";
import { DataStoreProvider } from "../core/provider";
import {
  useActivityDefinitionSubscription,
  useAllergyIntoleranceSubscription,
  useAppointmentSubscription,
  useBasicSubscription,
  useBookedSlotSubscription,
  useCarePlanSubscription,
  useClinicalImpressionSubscription,
  useCommunicationSubscription,
  useConditionSubscription,
  useDeviceDefinitionSubscription,
  useDeviceSubscription,
  useDocumentReferenceSubscription,
  useEncounterSubscription,
  useEpisodeSubscription,
  useFlagSubscription,
  useGoalSubscription,
  useLocationSubscription,
  useMedicationStatementSubscription,
  useObservationSubscription,
  usePatientSubscription,
  usePlanDefinitionSubscription,
  usePractitionerSubscription,
  useProcedureSubscription,
  useQuestionnaireInstanceSubscription,
  useQuestionnaireSubscription,
  useRelatedPersonSubscription,
  useRequestGroupSubscription,
  useScheduleSubscription,
  useServiceRequestSubscription,
  useSlotSubscription,
  useTaskSubscription,
} from "../core/subscription";
import { useHasConsumers } from "../core/use-has-consumers";
import { type CanRead, isCollectionReadable } from "./collection-scope";
import {
  createActivityDefinitionCollectionConfig,
  createAllergyIntoleranceCollectionConfig,
  createAppointmentCollectionConfig,
  createAvailabilityBlockCollectionConfig,
  createBasicCollectionConfig,
  createBookedSlotCollectionConfig,
  createCarePlanCollectionConfig,
  createClinicalImpressionCollectionConfig,
  createCommunicationCollectionConfig,
  createConditionCollectionConfig,
  createDeviceCollectionConfig,
  createDeviceDefinitionCollectionConfig,
  createDocumentReferenceCollectionConfig,
  createEncounterCollectionConfig,
  createEpisodeOfCareCollectionConfig,
  createFindingDefinitionCollectionConfig,
  createFlagCollectionConfig,
  createGoalCollectionConfig,
  createLocationCollectionConfig,
  createMedicationStatementCollectionConfig,
  createObservationCollectionConfig,
  createPatientCollectionConfig,
  createPlanDefinitionCollectionConfig,
  createPractitionerCollectionConfig,
  createProcedureCollectionConfig,
  createQuestionnaireCollectionConfig,
  createQuestionnaireInstanceCollectionConfig,
  createRelatedPersonCollectionConfig,
  createRequestGroupCollectionConfig,
  createScheduleCollectionConfig,
  createServiceRequestCollectionConfig,
  createSlotCollectionConfig,
  createTaskCollectionConfig,
} from "./collections";
import {
  ACTIVITY_DEFINITION_COLLECTION_NAME,
  ALLERGY_INTOLERANCE_COLLECTION_NAME,
  APPOINTMENT_COLLECTION_NAME,
  AVAILABILITY_BLOCK_COLLECTION_NAME,
  BASIC_COLLECTION_NAME,
  BOOKED_SLOT_COLLECTION_NAME,
  CARE_PLAN_COLLECTION_NAME,
  CLINICAL_IMPRESSION_COLLECTION_NAME,
  COMMUNICATION_COLLECTION_NAME,
  CONDITION_COLLECTION_NAME,
  DEVICE_COLLECTION_NAME,
  DEVICE_DEFINITION_COLLECTION_NAME,
  DOCUMENT_REFERENCE_COLLECTION_NAME,
  ENCOUNTER_COLLECTION_NAME,
  EPISODE_OF_CARE_COLLECTION_NAME,
  FLAG_COLLECTION_NAME,
  GOAL_COLLECTION_NAME,
  LOCATION_COLLECTION_NAME,
  MEDICATION_STATEMENT_COLLECTION_NAME,
  OBSERVATION_COLLECTION_NAME,
  PATIENT_COLLECTION_NAME,
  PLAN_DEFINITION_COLLECTION_NAME,
  PRACTITIONER_COLLECTION_NAME,
  PROCEDURE_COLLECTION_NAME,
  QUESTIONNAIRE_COLLECTION_NAME,
  QUESTIONNAIRE_RESPONSE_COLLECTION_NAME,
  RELATED_PERSON_COLLECTION_NAME,
  REQUEST_GROUP_COLLECTION_NAME,
  SCHEDULE_COLLECTION_NAME,
  SERVICE_REQUEST_COLLECTION_NAME,
  SLOT_COLLECTION_NAME,
  TASK_COLLECTION_NAME,
} from "./constants";

export interface FhirDataStoreProviderProps {
  children: ReactNode;
  /** Enable real-time WebSocket subscriptions (default: true) */
  enableSubscriptions?: boolean;
  /**
   * Restrict synced + subscribed collections to those whose FHIR resource type
   * this predicate accepts (typically `permissions.canRead`). Omit to register
   * every collection — the provider-app default, where the session can read
   * everything the registry syncs.
   *
   * Scoping keeps a narrow-AccessPolicy session (e.g. the patient portal) from
   * eagerly searching + subscribing to resource types it can't read, which the
   * server rejects with a storm of 403s.
   */
  canRead?: CanRead;
}

/**
 * Sticky variant of {@link useHasConsumers}: once a collection has had a live
 * consumer this session, it stays "true" for the life of the provider.
 *
 * Why: each subscription is gated on `enableSubscriptions && hasConsumers`.
 * Raw `hasConsumers` flaps true→false→true as `useCollection` consumers mount
 * and unmount during a route render. When the combined criteria set momentarily
 * empties, Medplum's shared SubscriptionManager closes its WebSocket and reopens
 * a new one — a socket/subscription churn storm that (a) leaves the server's
 * active-subscription set pointing at superseded sockets, so change
 * notifications never reach the live socket, and (b) leaks toward the per-user
 * WS subscription cap. Latching the gate makes the criteria set monotonic
 * (false→true only), so the socket opens once and stays open.
 */
function useStickyHasConsumers(name: string): boolean {
  const has = useHasConsumers(name);
  const everHad = useRef(false);
  if (has) {
    everHad.current = true;
  }
  return everHad.current;
}

/** Internal component that sets up subscriptions inside DataStoreProvider context */
function RelatedPersonSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(RELATED_PERSON_COLLECTION_NAME);
  useRelatedPersonSubscription(enabled && hasConsumers);
  return null;
}

function PatientSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(PATIENT_COLLECTION_NAME);
  usePatientSubscription(enabled && hasConsumers);
  return null;
}

function EpisodeSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(EPISODE_OF_CARE_COLLECTION_NAME);
  useEpisodeSubscription(enabled && hasConsumers);
  return null;
}

function ScheduleSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(SCHEDULE_COLLECTION_NAME);
  useScheduleSubscription(enabled && hasConsumers);
  return null;
}

function SlotSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(SLOT_COLLECTION_NAME);
  useSlotSubscription(enabled && hasConsumers);
  return null;
}

function QuestionnaireSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(QUESTIONNAIRE_COLLECTION_NAME);
  useQuestionnaireSubscription(enabled && hasConsumers);
  return null;
}

function QuestionnaireResponseSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(QUESTIONNAIRE_RESPONSE_COLLECTION_NAME);
  useQuestionnaireInstanceSubscription(enabled && hasConsumers);
  return null;
}

function ActivityDefinitionSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(ACTIVITY_DEFINITION_COLLECTION_NAME);
  useActivityDefinitionSubscription(enabled && hasConsumers);
  return null;
}

function LocationSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(LOCATION_COLLECTION_NAME);
  useLocationSubscription(enabled && hasConsumers);
  return null;
}

function MedicationStatementSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(MEDICATION_STATEMENT_COLLECTION_NAME);
  useMedicationStatementSubscription(enabled && hasConsumers);
  return null;
}

function AllergyIntoleranceSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(ALLERGY_INTOLERANCE_COLLECTION_NAME);
  useAllergyIntoleranceSubscription(enabled && hasConsumers);
  return null;
}

function DocumentReferenceSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(DOCUMENT_REFERENCE_COLLECTION_NAME);
  useDocumentReferenceSubscription(enabled && hasConsumers);
  return null;
}

function DeviceSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(DEVICE_COLLECTION_NAME);
  useDeviceSubscription(enabled && hasConsumers);
  return null;
}

function DeviceDefinitionSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(DEVICE_DEFINITION_COLLECTION_NAME);
  useDeviceDefinitionSubscription(enabled && hasConsumers);
  return null;
}

function EncounterSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(ENCOUNTER_COLLECTION_NAME);
  useEncounterSubscription(enabled && hasConsumers);
  return null;
}

function ObservationSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(OBSERVATION_COLLECTION_NAME);
  useObservationSubscription(enabled && hasConsumers);
  return null;
}

function AppointmentSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(APPOINTMENT_COLLECTION_NAME);
  useAppointmentSubscription(enabled && hasConsumers);
  return null;
}

function BasicSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(BASIC_COLLECTION_NAME);
  useBasicSubscription(enabled && hasConsumers);
  return null;
}

function BookedSlotSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(BOOKED_SLOT_COLLECTION_NAME);
  useBookedSlotSubscription(enabled && hasConsumers);
  return null;
}

function CarePlanSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(CARE_PLAN_COLLECTION_NAME);
  useCarePlanSubscription(enabled && hasConsumers);
  return null;
}

function GoalSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(GOAL_COLLECTION_NAME);
  useGoalSubscription(enabled && hasConsumers);
  return null;
}

function FlagSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(FLAG_COLLECTION_NAME);
  useFlagSubscription(enabled && hasConsumers);
  return null;
}

function CommunicationSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(COMMUNICATION_COLLECTION_NAME);
  useCommunicationSubscription(enabled && hasConsumers);
  return null;
}

function ConditionSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(CONDITION_COLLECTION_NAME);
  useConditionSubscription(enabled && hasConsumers);
  return null;
}

function ClinicalImpressionSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(CLINICAL_IMPRESSION_COLLECTION_NAME);
  useClinicalImpressionSubscription(enabled && hasConsumers);
  return null;
}

function PlanDefinitionSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(PLAN_DEFINITION_COLLECTION_NAME);
  usePlanDefinitionSubscription(enabled && hasConsumers);
  return null;
}

function PractitionerSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(PRACTITIONER_COLLECTION_NAME);
  usePractitionerSubscription(enabled && hasConsumers);
  return null;
}

function ProcedureSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(PROCEDURE_COLLECTION_NAME);
  useProcedureSubscription(enabled && hasConsumers);
  return null;
}

function TaskSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(TASK_COLLECTION_NAME);
  useTaskSubscription(enabled && hasConsumers);
  return null;
}

function RequestGroupSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(REQUEST_GROUP_COLLECTION_NAME);
  useRequestGroupSubscription(enabled && hasConsumers);
  return null;
}

function ServiceRequestSubscription({ enabled }: { enabled: boolean }) {
  const hasConsumers = useStickyHasConsumers(SERVICE_REQUEST_COLLECTION_NAME);
  useServiceRequestSubscription(enabled && hasConsumers);
  return null;
}

/**
 * Every collection subscription, paired with its registry name so the set can be
 * filtered by the same `canRead` predicate that gates registration. Each component
 * already gates itself on `useStickyHasConsumers`; scoping here just avoids
 * mounting a subscription for a resource type the session can't read.
 */
const SUBSCRIPTIONS: ReadonlyArray<{ name: string; Component: FC<{ enabled: boolean }> }> = [
  { name: PATIENT_COLLECTION_NAME, Component: PatientSubscription },
  { name: RELATED_PERSON_COLLECTION_NAME, Component: RelatedPersonSubscription },
  { name: EPISODE_OF_CARE_COLLECTION_NAME, Component: EpisodeSubscription },
  { name: SCHEDULE_COLLECTION_NAME, Component: ScheduleSubscription },
  { name: APPOINTMENT_COLLECTION_NAME, Component: AppointmentSubscription },
  { name: BASIC_COLLECTION_NAME, Component: BasicSubscription },
  { name: BOOKED_SLOT_COLLECTION_NAME, Component: BookedSlotSubscription },
  { name: SLOT_COLLECTION_NAME, Component: SlotSubscription },
  { name: QUESTIONNAIRE_COLLECTION_NAME, Component: QuestionnaireSubscription },
  { name: QUESTIONNAIRE_RESPONSE_COLLECTION_NAME, Component: QuestionnaireResponseSubscription },
  { name: ACTIVITY_DEFINITION_COLLECTION_NAME, Component: ActivityDefinitionSubscription },
  { name: LOCATION_COLLECTION_NAME, Component: LocationSubscription },
  { name: MEDICATION_STATEMENT_COLLECTION_NAME, Component: MedicationStatementSubscription },
  { name: ALLERGY_INTOLERANCE_COLLECTION_NAME, Component: AllergyIntoleranceSubscription },
  { name: DEVICE_COLLECTION_NAME, Component: DeviceSubscription },
  { name: DEVICE_DEFINITION_COLLECTION_NAME, Component: DeviceDefinitionSubscription },
  { name: DOCUMENT_REFERENCE_COLLECTION_NAME, Component: DocumentReferenceSubscription },
  { name: ENCOUNTER_COLLECTION_NAME, Component: EncounterSubscription },
  { name: OBSERVATION_COLLECTION_NAME, Component: ObservationSubscription },
  { name: COMMUNICATION_COLLECTION_NAME, Component: CommunicationSubscription },
  { name: CONDITION_COLLECTION_NAME, Component: ConditionSubscription },
  { name: CARE_PLAN_COLLECTION_NAME, Component: CarePlanSubscription },
  { name: GOAL_COLLECTION_NAME, Component: GoalSubscription },
  { name: FLAG_COLLECTION_NAME, Component: FlagSubscription },
  { name: CLINICAL_IMPRESSION_COLLECTION_NAME, Component: ClinicalImpressionSubscription },
  { name: PLAN_DEFINITION_COLLECTION_NAME, Component: PlanDefinitionSubscription },
  { name: PRACTITIONER_COLLECTION_NAME, Component: PractitionerSubscription },
  { name: TASK_COLLECTION_NAME, Component: TaskSubscription },
  { name: PROCEDURE_COLLECTION_NAME, Component: ProcedureSubscription },
  { name: REQUEST_GROUP_COLLECTION_NAME, Component: RequestGroupSubscription },
  { name: SERVICE_REQUEST_COLLECTION_NAME, Component: ServiceRequestSubscription },
];

/**
 * FHIR-specific React provider for the data store.
 * Must be nested inside MedplumProvider.
 * Registers every FHIR collection plus optional real-time subscriptions. Pass
 * `canRead` to scope a narrow AccessPolicy: unreadable collections stay registered
 * (so consumers resolve them) but skip their server fetch and subscription.
 */
export function FhirDataStoreProvider({ children, enableSubscriptions = true, canRead }: FhirDataStoreProviderProps) {
  const medplum = useMedplum();

  const client = useMemo(() => {
    const c = new DataStoreClient();
    // Every collection is registered so consumers always resolve it, even under a
    // narrow AccessPolicy. `canRead` is threaded into each config: an unreadable
    // resource type stays registered but skips its server fetch (see base.ts),
    // so views that join across it get an empty collection instead of a 403.
    c.registerCollection(
      ACTIVITY_DEFINITION_COLLECTION_NAME,
      createActivityDefinitionCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(APPOINTMENT_COLLECTION_NAME, createAppointmentCollectionConfig({ medplum, canRead }));
    c.registerCollection(
      AVAILABILITY_BLOCK_COLLECTION_NAME,
      createAvailabilityBlockCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(BASIC_COLLECTION_NAME, createBasicCollectionConfig({ medplum, canRead }));
    c.registerCollection(BOOKED_SLOT_COLLECTION_NAME, createBookedSlotCollectionConfig({ medplum, canRead }));
    c.registerCollection(SLOT_COLLECTION_NAME, createSlotCollectionConfig({ medplum, canRead }));
    c.registerCollection(EPISODE_OF_CARE_COLLECTION_NAME, createEpisodeOfCareCollectionConfig({ medplum, canRead }));
    c.registerCollection(PATIENT_COLLECTION_NAME, createPatientCollectionConfig({ medplum, canRead }));
    c.registerCollection("findingDefinition", createFindingDefinitionCollectionConfig({ medplum, canRead }));
    c.registerCollection(RELATED_PERSON_COLLECTION_NAME, createRelatedPersonCollectionConfig({ medplum, canRead }));
    c.registerCollection(QUESTIONNAIRE_COLLECTION_NAME, createQuestionnaireCollectionConfig({ medplum, canRead }));
    c.registerCollection(
      QUESTIONNAIRE_RESPONSE_COLLECTION_NAME,
      createQuestionnaireInstanceCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(SCHEDULE_COLLECTION_NAME, createScheduleCollectionConfig({ medplum, canRead }));
    c.registerCollection(LOCATION_COLLECTION_NAME, createLocationCollectionConfig({ medplum, canRead }));
    c.registerCollection(
      MEDICATION_STATEMENT_COLLECTION_NAME,
      createMedicationStatementCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(
      ALLERGY_INTOLERANCE_COLLECTION_NAME,
      createAllergyIntoleranceCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(DEVICE_COLLECTION_NAME, createDeviceCollectionConfig({ medplum, canRead }));
    c.registerCollection(
      DEVICE_DEFINITION_COLLECTION_NAME,
      createDeviceDefinitionCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(
      DOCUMENT_REFERENCE_COLLECTION_NAME,
      createDocumentReferenceCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(ENCOUNTER_COLLECTION_NAME, createEncounterCollectionConfig({ medplum, canRead }));
    c.registerCollection(OBSERVATION_COLLECTION_NAME, createObservationCollectionConfig({ medplum, canRead }));
    c.registerCollection(COMMUNICATION_COLLECTION_NAME, createCommunicationCollectionConfig({ medplum, canRead }));
    c.registerCollection(CONDITION_COLLECTION_NAME, createConditionCollectionConfig({ medplum, canRead }));
    c.registerCollection(CARE_PLAN_COLLECTION_NAME, createCarePlanCollectionConfig({ medplum, canRead }));
    c.registerCollection(GOAL_COLLECTION_NAME, createGoalCollectionConfig({ medplum, canRead }));
    c.registerCollection(FLAG_COLLECTION_NAME, createFlagCollectionConfig({ medplum, canRead }));
    c.registerCollection(
      CLINICAL_IMPRESSION_COLLECTION_NAME,
      createClinicalImpressionCollectionConfig({ medplum, canRead }),
    );
    c.registerCollection(PLAN_DEFINITION_COLLECTION_NAME, createPlanDefinitionCollectionConfig({ medplum, canRead }));
    c.registerCollection(PRACTITIONER_COLLECTION_NAME, createPractitionerCollectionConfig({ medplum, canRead }));
    c.registerCollection(TASK_COLLECTION_NAME, createTaskCollectionConfig({ medplum, canRead }));
    c.registerCollection(PROCEDURE_COLLECTION_NAME, createProcedureCollectionConfig({ medplum, canRead }));
    c.registerCollection(REQUEST_GROUP_COLLECTION_NAME, createRequestGroupCollectionConfig({ medplum, canRead }));
    c.registerCollection(SERVICE_REQUEST_COLLECTION_NAME, createServiceRequestCollectionConfig({ medplum, canRead }));
    return c;
  }, [medplum, canRead]);

  return (
    <DataStoreProvider client={client}>
      {SUBSCRIPTIONS.filter(({ name }) => isCollectionReadable(name, canRead)).map(({ name, Component }) => (
        <Component key={name} enabled={enableSubscriptions} />
      ))}
      {children}
    </DataStoreProvider>
  );
}
